import React from "react";

import {
  Box,
  Typography,
  Button,
  Card,
  useTheme,
  TextField,
  CircularProgress,
  Grid,
  Alert,
  Stack,
  Divider,
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import InputMask from "react-input-mask";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { visitorService } from "../../../shared/services/visitors/visitorService";
import { useToast } from "../../../shared/hooks/useToast";
import { useQuery } from "@tanstack/react-query";
import { eventService } from "../../../shared/services/tenant/eventService";
import { trackPaymentInitiated } from "../../../shared/analytics/gtm";
import type { TrackableEvent } from "../../../shared/analytics/types";

// Função para formatar CPF
const formatCPF = (cpf: string) => {
  const cleanCPF = cpf.replace(/\D/g, "");
  if (cleanCPF.length === 11) {
    return `${cleanCPF.slice(0, 3)}.${cleanCPF.slice(3, 6)}.${cleanCPF.slice(6, 9)}-${cleanCPF.slice(9, 11)}`;
  }
  return cpf;
};

export default function BoletoPaymentPage() {
  const navigate = useNavigate();
  const { id, slug } = useParams();
  const idOrSlug = slug || id;
  const theme = useTheme();
  const location = useLocation();
  const { showToast } = useToast();

  // Determina o caminho base (novo com slug ou antigo com id)
  const basePath = slug ? `/evento/${slug}` : `/public/event/${id}`;

  const cpf = location.state?.cpf || localStorage.getItem("lastCpf") || "";
  const [loading, setLoading] = useState(false);
  // 🔒 PROTEÇÃO: Ref para evitar double-submit
  const isProcessingRef = React.useRef(false);

  // Buscar dados do evento
  const { data: event } = useQuery({
    queryKey: ["event", idOrSlug],
    queryFn: () => eventService.getPublic(idOrSlug!).then((res) => res.data),
    enabled: !!idOrSlug,
  });

  // Calcular data limite para vencimento (1 dia antes do evento)
  const getMaxDueDate = () => {
    if (!event?.start_date) return null;

    const eventDate = new Date(event.start_date + "T00:00:00"); // Força horário local
    const maxDueDate = new Date(eventDate);
    maxDueDate.setDate(maxDueDate.getDate() - 1); // 1 dia antes do evento

    return maxDueDate;
  };

  // Verificar se ainda é possível gerar boleto
  const canGenerateBoleto = () => {
    const maxDueDate = getMaxDueDate();
    if (!maxDueDate) return true;

    const today = new Date();
    today.setHours(23, 59, 59, 999); // Define para o final do dia de hoje
    maxDueDate.setHours(23, 59, 59, 999); // Define para o final do dia limite

    return today <= maxDueDate;
  };

  const getMaxInstallments = () => {
    if (!event?.start_date) return 1;

    const today = new Date();
    const eventDate = new Date(event.start_date + "T00:00:00");

    // Diferença em dias entre hoje e o evento
    const diffTime = eventDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    // Se tem menos de 30 dias, só pode ser à vista
    if (diffDays < 30) return 1;

    // Cada parcela precisa de pelo menos 30 dias para vencimento
    // Máximo de parcelas considerando que todas devem vencer antes do evento
    const maxParcelasByDate = Math.floor(diffDays / 30);

    // Usa o máximo configurado no evento (padrão: 12) ou o calculado pela data, o que for menor
    const maxConfigured = event?.max_installments_boleto || 12;
    
    // Retorna o menor valor entre o configurado e o calculado pela data
    const maxParcelas = Math.min(maxParcelasByDate, maxConfigured);

    // Garante que não seja maior que o configurado
    return Math.min(maxParcelas, maxConfigured);
  };

  const maxInstallments = getMaxInstallments();

  const validationSchema = Yup.object().shape({
    participantCpf: Yup.string()
      .required("CPF obrigatório")
      .test("cpf-valid", "CPF inválido", (value) => {
        const cleaned = value?.replace(/\D/g, "") || "";
        return cleaned.length === 11;
      }),
    description: Yup.string().required("Descrição obrigatória"),
    externalReference: Yup.string().required("Referência obrigatória"),
    installmentCount: Yup.number()
      .required("Número de parcelas obrigatório")
      .min(1, "Mínimo 1 parcela")
      .max(
        maxInstallments,
        `Máximo ${maxInstallments} parcelas para este evento`
      ),
  });

  const generateReference = () => {
    return `AVIVA-${Date.now()}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
  };

  const handleSubmit = async (values: any) => {
    console.log("submit chamado", values);

    // 🔒 PROTEÇÃO: Evitar múltiplas submissões simultâneas
    if (isProcessingRef.current) {
      console.warn(
        "⚠️ Requisição já está sendo processada - ignorando clique duplicado"
      );
      showToast("Aguarde, processando...", "info");
      return;
    }

    if (!canGenerateBoleto()) {
      showToast(
        "Não é mais possível gerar boleto para este evento. O prazo para pagamento via boleto expirou.",
        "error"
      );
      return;
    }

    // Marca como processando
    isProcessingRef.current = true;
    setLoading(true);

    try {
      const paymentData = {
        cpf: values.participantCpf.replace(/\D/g, ""),
        eventId: event?.id || id!,
        installmentCount: parseInt(values.installmentCount),
        description: values.description,
        externalReference: values.externalReference,
      };

      console.log("Enviando dados:", paymentData);
      const response = await visitorService.boletoPayment(paymentData);
      console.log("Resposta do servidor:", response);

      // Apenas toast baseado no status
      if (response.payment?.status === "EXISTING_BOLETO") {
        showToast("Boleto já existe e está disponível para pagamento!", "info");
      } else {
        showToast("Boleto gerado com sucesso!", "success");
      }
      if (event) {
        trackPaymentInitiated(event as TrackableEvent, "boleto");
      }
    } catch (error: any) {
      console.log("Erro capturado:", error);
      const errorMessage =
        error.response?.data?.message || "Erro ao gerar boleto";

      // Verifica se é erro de pagamento já processado ou duplicação
      if (
        errorMessage.includes("Pagamento já foi processado") ||
        errorMessage.includes("já foi realizado") ||
        errorMessage.includes("duplicate key") ||
        errorMessage.includes("IDX_INSTALLMENTS_UNIQUE")
      ) {
        showToast(
          "Pagamento já foi processado para este participante!",
          "info"
        );
      } else {
        showToast(errorMessage, "error");
      }
    } finally {
      setLoading(false);
      // 🔒 Libera após 2 segundos para evitar clicks muito rápidos
      setTimeout(() => {
        isProcessingRef.current = false;
      }, 2000);
    }
  };

  // Renderização principal
  if (!canGenerateBoleto()) {
    return (
      <Box
        sx={{
          p: { xs: 1, md: 4 },
          background: theme.palette.background.default,
          minHeight: "100vh",
        }}
      >
        <Card
          sx={{
            background: theme.palette.background.paper,
            borderRadius: 4,
            p: 4,
            maxWidth: 600,
            margin: "32px auto",
            boxShadow: "0 2px 8px 0 rgba(16,30,54,0.04)",
            textAlign: "center",
          }}
        >
          <Stack alignItems="center" spacing={3} sx={{ py: 4 }}>
            <ReceiptIcon sx={{ fontSize: 80, color: "error.main" }} />
            <Typography variant="h4" fontWeight={700} color="error.main">
              Prazo Expirado
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 500, textAlign: "center" }}
            >
              O prazo para pagamento via boleto expirou. O boleto precisa ser
              pago até 1 dia antes do evento.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(`${basePath}/payment`)}
            >
              Voltar
            </Button>
          </Stack>
        </Card>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          p: { xs: 1, md: 4 },
          background: theme.palette.background.default,
          minHeight: "100vh",
        }}
      >
        <Card
          sx={{
            background: theme.palette.background.paper,
            borderRadius: 4,
            p: 4,
            maxWidth: 700,
            margin: "32px auto",
            boxShadow: "0 2px 8px 0 rgba(16,30,54,0.04)",
            textAlign: "center",
          }}
        >
          <Stack alignItems="center" spacing={3} sx={{ py: 8 }}>
            <CircularProgress size={60} />
            <Typography variant="h6" color="primary">
              Gerando boleto...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Aguarde enquanto processamos sua solicitação
            </Typography>
          </Stack>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 1, md: 4 },
        background: theme.palette.background.default,
        minHeight: "100vh",
      }}
    >
      <Card
        sx={{
          background: theme.palette.background.paper,
          borderRadius: 4,
          p: 4,
          maxWidth: 700,
          margin: "32px auto",
          boxShadow: "0 2px 8px 0 rgba(16,30,54,0.04)",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ mb: 4, textAlign: "center", justifyContent: "center" }}
        >
          <ReceiptIcon sx={{ fontSize: 32, color: "primary.main" }} />
          <Typography variant="h4" fontWeight={700} color="primary.main">
            Pagamento via Boleto
          </Typography>
        </Stack>

        {event && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Importante:</strong> O boleto deve ser pago até{" "}
                <strong>{getMaxDueDate()?.toLocaleDateString("pt-BR")}</strong>
                (1 dia antes do evento).
                {maxInstallments > 1
                  ? ` Disponível parcelamento em até ${maxInstallments}x.`
                  : " Apenas pagamento à vista disponível."}
              </Typography>
            </Alert>

            {/* Informação sobre Juros e Multa */}
            {((event.boleto_interest && Number(event.boleto_interest) > 0) || 
              (event.boleto_fine && Number(event.boleto_fine) > 0)) && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Atenção:</strong> Em caso de atraso no pagamento, serão aplicados:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                  {event.boleto_interest && Number(event.boleto_interest) > 0 && (
                    <li>Juros de <strong>{Number(event.boleto_interest).toFixed(1).replace('.', ',')}% ao mês</strong></li>
                  )}
                  {event.boleto_fine && Number(event.boleto_fine) > 0 && (
                    <li>Multa de <strong>{Number(event.boleto_fine).toFixed(1).replace('.', ',')}%</strong> sobre o valor</li>
                  )}
                </Typography>
              </Alert>
            )}
          </>
        )}

        <Formik
          initialValues={{
            participantCpf: formatCPF(cpf),
            description: event
              ? `Ingresso para ${event.title}`
              : "Ingresso para evento",
            externalReference: generateReference(),
            installmentCount: 1,
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            setFieldValue,
          }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                    Dados do Pagamento
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                </Grid>

                <Grid item xs={12} md={6}>
                  <InputMask
                    mask="999.999.999-99"
                    value={values.participantCpf}
                    onChange={(e) =>
                      setFieldValue("participantCpf", e.target.value)
                    }
                    onBlur={handleBlur}
                  >
                    {(inputProps: any) => (
                      <TextField
                        {...inputProps}
                        fullWidth
                        name="participantCpf"
                        label="CPF do participante"
                        error={
                          touched.participantCpf && !!errors.participantCpf
                        }
                        helperText={
                          touched.participantCpf && errors.participantCpf
                        }
                        placeholder="000.000.000-00"
                      />
                    )}
                  </InputMask>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    name="installmentCount"
                    label="Número de parcelas"
                    value={values.installmentCount}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.installmentCount && !!errors.installmentCount
                    }
                    helperText={
                      touched.installmentCount && errors.installmentCount
                    }
                    SelectProps={{
                      native: true,
                    }}
                  >
                    {Array.from(
                      { length: maxInstallments },
                      (_, i) => i + 1
                    ).map((num) => (
                      <option key={num} value={num}>
                        {num}x
                      </option>
                    ))}
                  </TextField>
                </Grid>

                {/* <Grid item xs={12} md={6} >
                                    <TextField
                                        fullWidth
                                        name="externalReference"
                                        label="Referência"
                                        value={values.externalReference}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.externalReference && !!errors.externalReference}
                                        helperText={touched.externalReference && errors.externalReference}
                                        placeholder="Referência única"
                                    />
                                </Grid> */}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    name="description"
                    label="Descrição do pagamento"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.description && !!errors.description}
                    helperText={touched.description && errors.description}
                    placeholder="Descrição do que está sendo pago"
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      justifyContent: "center",
                      mt: 4,
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate(`${basePath}/payment`)}
                      disabled={loading}
                    >
                      Voltar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading}
                      sx={{ minWidth: 200 }}
                    >
                      Gerar Boleto
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Card>
    </Box>
  );
}
